/**
 * @file        kybConstants.js
 * @module      modules/kyb/utils
 * @description Shared labels for the KYB flow: the 6-step rail, which stage
 *              each screen belongs to, status wording, loader messages
 *              (functional scope 13.1) and client-side field rules (14.1).
 *              The backend re-checks everything; these only give instant
 *              feedback.
 */

export const RAIL = [
  { title: "Business", hint: "PAN, GST and registry" },
  { title: "Your identity", hint: "Aadhaar or DigiLocker" },
  { title: "Link to business", hint: "Registry match" },
  { title: "Authority", hint: "Only if needed" },
  { title: "Additional documents", hint: "Only if needed" },
  { title: "Result", hint: "Decision" },
];

export const CHECK_KEYS = ["business", "individual", "association", "authority"];

export const SCREEN_STAGE = {
  consent: 0,
  pan: 0,
  panResult: 0,
  gstResult: 0,
  cin: 0,
  llpin: 0,
  registryResult: 0,
  docUpload: 0,
  otherDocs: 0,
  bizDone: 0,
  join: 0,
  aadhaar: 1,
  aadhaarOtp: 1,
  panMatch: 1,
  digilocker: 1,
  dlWait: 1,
  dlIssue: 1,
  altDoc: 1,
  altSelfie: 1,
  altResult: 1,
  digiResult: 1,
  link: 2,
  authority: 3,
  authorityDone: 3,
  manualVerify: 4,
  decision: 5,
};

export const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  awaiting_approval: "Awaiting approval",
  verified: "Verified",
  review_required: "Review required",
  not_matched: "Not matched",
  declined: "Declined",
};

/** Check status → rail/section appearance: [class, icon, label]. */
export const RAIL_STATE = {
  verified: ["ok", "✓", "Passed"],
  review_required: ["rev", "!", "Needs review"],
  not_matched: ["bad", "✕", "Failed"],
  declined: ["bad", "✕", "Failed"],
  awaiting_approval: ["prog", "⋯", "Ongoing"],
  in_progress: ["prog", "", "Ongoing"],
};

export const NEXT_DESC = [
  "",
  "Verify your identity with your Aadhaar. It takes about a minute.",
  "We check whether you're a director, proprietor or partner.",
  "Show you're allowed to act for the business.",
  "Upload documents for our team to verify.",
  "See whether your business is approved.",
];

export const LOADING = {
  pan: "Verifying PAN and finding GST registrations",
  panOnly: "Verifying PAN",
  retryGst: "Finding GST registrations",
  gstin: "Fetching GST details",
  cin: "Fetching company record",
  llpin: "Saving your LLPIN",
  udyam: "Looking for a Udyam registration",
  submitDocs: "Submitting for review",
  upload: "Uploading your document",
  template: "Preparing your template",
  generic: "Just a moment",
  status: "Checking your verification status",
  otpSend: "Sending an OTP to your Aadhaar-linked mobile",
  otpVerify: "Verifying your Aadhaar and PAN",
  pan360: "Checking your PAN against your Aadhaar",
  dlOpen: "Opening DigiLocker",
  dlReturn: "Returning to ASAP · Fetching your documents",
  dlBack: "You're back on ASAP · Checking your DigiLocker session",
  altDoc: "Reading and checking your document",
  selfie: "Checking your selfie",
};

export const PATTERN = {
  PAN: /^[A-Z]{5}\d{4}[A-Z]$/,
  CIN: /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/,
  LLPIN: /^[A-Z]{3}-\d{4}$/,
  AADHAAR: /^[2-9]\d{11}$/,
  PERSONAL_PAN: /^[A-Z]{3}P[A-Z]\d{4}[A-Z]$/,
};

export const MSG = {
  PAN_FORMAT: "Enter a valid 10-character PAN, for example AABCA1234F.",
  CIN_FORMAT: "Enter a valid 21-character CIN, for example U12345DL2019PTC123456.",
  LLPIN_FORMAT: "Enter a valid LLPIN, for example AAB-1234.",
  CONSENT: "Agree to the checks to continue.",
  UPLOAD_TYPE: "Upload a PDF, JPG or PNG file.",
  NETWORK: "We couldn't reach ASAP just now. Try again.",
  AADHAAR_FORMAT: "Enter your 12-digit Aadhaar number.",
  PERSONAL_PAN: "Enter your personal PAN. The 4th character of a personal PAN is P.",
  OTP_FORMAT: "Enter the 6-digit OTP sent to your Aadhaar-linked mobile.",
  ID_UPLOAD_TYPE: "Upload a photo of your document (JPG or PNG).",
  PASSPORT_FILE_NUMBER: "Enter the file number printed on the back page of your passport.",
};

/** Where "Go to dashboard" leads: the app's post-login landing page (App.js /home). */
export const DASHBOARD_PATH = "/home";

export const ID_UPLOAD_TYPES = ["image/jpeg", "image/png"];

/** How often the waiting screen asks the backend for the DigiLocker status. */
export const DL_POLL_MS = 5000;

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png"];

/** Simulator drawer groups (functional scope 17.1). */
export const SIM_GROUPS = [
  {
    title: "Business records",
    controls: [
      ["dup", "PAN already verified on ASAP"],
      ["pan", "Verify PAN response"],
      ["gst", "GSTINs linked to PAN"],
      ["gstLookup", "GST lookup response"],
      ["gstStatus", "Delhi GSTIN status"],
      ["constitution", "GST constitution vs PAN"],
      ["cin", "Verify CIN response"],
      ["cinStatus", "CIN status"],
      ["registryName", "Registry name check"],
      ["udyam", "Udyam linked to PAN"],
      ["udyamSize", "Udyam enterprise size"],
      ["udyamActivity", "Udyam major activity"],
    ],
  },
  {
    title: "Your identity",
    controls: [
      ["aadhaarOtp", "Aadhaar OTP send"],
      ["aadhaarVerify", "Aadhaar OTP verify"],
      ["pan360", "PAN 360 (personal PAN)"],
      ["dlOutcome", "DigiLocker session status"],
      ["dlAadhaar", "Aadhaar in DigiLocker (eaadhaar)"],
      ["altOcr", "Document OCR / verification"],
      ["face", "Selfie liveness / face match"],
      ["person", "DigiLocker person"],
    ],
  },
];

/** Rail index → tab on the Result screen (the rail is clickable there). */
export const RESULT_TABS = ["business", "individual", "association", "authority", "documents", "result"];

/** Customer-facing names for each kind of uploaded file. */
export const DOC_KIND_LABEL = {
  business_registration: "Business registration",
  other_registration: "Registration document",
  other_authorization: "Authorizing resolution",
  id_front: "ID document (front)",
  id_back: "ID document (back)",
  selfie: "Selfie",
  authority_letter: "Authorization letter",
  authority_supporting: "Supporting document",
  fallback_document: "Additional document",
  requested_document: "Requested by our team",
};

/** Files that belong on the "Additional documents" step: asked for by the KYB team, or sent on the Additional documents form. */
export const ADDITIONAL_KINDS = ["requested_document", "fallback_document"];
export const additionalUploads = (view) => (view.uploads || []).filter((u) => ADDITIONAL_KINDS.includes(u.kind));

/** Which step each file belongs to, for the Result tabs and the documents list. */
export const DOC_GROUPS = [
  { key: "business", title: "Business", kinds: ["business_registration", "other_registration", "other_authorization"] },
  { key: "individual", title: "Your identity", kinds: ["id_front", "id_back", "selfie"] },
  { key: "authority", title: "Authority", kinds: ["authority_letter", "authority_supporting"] },
  { key: "additional", title: "Additional documents", kinds: ["fallback_document", "requested_document"] },
];

// GST state codes — the first two characters of a GSTIN (GSTIN entry when the
// GST lookup from the PAN fails). Keep in step with GST_STATE_CODES on the server.
export const GST_STATES = [
  ["01", "Jammu and Kashmir"], ["02", "Himachal Pradesh"], ["03", "Punjab"], ["04", "Chandigarh"], ["05", "Uttarakhand"],
  ["06", "Haryana"], ["07", "Delhi"], ["08", "Rajasthan"], ["09", "Uttar Pradesh"], ["10", "Bihar"],
  ["11", "Sikkim"], ["12", "Arunachal Pradesh"], ["13", "Nagaland"], ["14", "Manipur"], ["15", "Mizoram"],
  ["16", "Tripura"], ["17", "Meghalaya"], ["18", "Assam"], ["19", "West Bengal"], ["20", "Jharkhand"],
  ["21", "Odisha"], ["22", "Chhattisgarh"], ["23", "Madhya Pradesh"], ["24", "Gujarat"], ["25", "Daman and Diu"],
  ["26", "Dadra and Nagar Haveli and Daman and Diu"], ["27", "Maharashtra"], ["28", "Andhra Pradesh (before 2014)"], ["29", "Karnataka"], ["30", "Goa"],
  ["31", "Lakshadweep"], ["32", "Kerala"], ["33", "Tamil Nadu"], ["34", "Puducherry"], ["35", "Andaman and Nicobar Islands"],
  ["36", "Telangana"], ["37", "Andhra Pradesh"], ["38", "Ladakh"], ["97", "Other Territory"], ["99", "Centre Jurisdiction"],
];
